import moment from "moment"
import type { Moment } from "moment"
import {
  addMonths,
  endOfMonth,
  endOfYear,
  isAfter,
  isBefore,
  isSameDay,
  max as maxDate,
  min as minDate,
  startOfDay,
  startOfMonth,
  startOfYear,
} from "date-fns"
import { formatDate, DateFormat } from "./date"

// ============================
// Types
// ============================

// for string value: valid date string or MM-YYYY
export type DateInput = string | number | Date

type StateInput =
  | { date: DateInput }
  | { dateFrom: DateInput; dateTo: DateInput }
  | { year: number }
  | { year: number; months: number[] } // 1-12
  | { year: number; month: number } // 1-12

/** Optional resolver options. capDate defaults to new Date() if not provided. */
export type ResolveDateOptions = {
  /** Safety cap: no resolved date may exceed this. Default: new Date() */
  capDate?: DateInput|true|null
}

type OutputOption = DateFormat | Date

type ObjOutput = {
  state: StateOutput
  getRange: (format: OutputOption) => { dateFrom: OutputOption; dateTo: OutputOption }
  getSingleYear: () => number
  getSingleMonth: () => { year: number; month: number }
  getYearMonths: () => { year: number; months: number[] }
  getSingleDay: (format: OutputOption) => OutputOption
}

type StateOutput = {
  range: { dateFrom: string; dateTo: string } // YYYY-MM-DD
  year: number
  yearMonths: { year: number; months: number[] } // 1-12
  yearMonth: { year: number; month: number; date: string } // YYYY-MM
  date: string // YYYY-MM-DD
}

// ============================
// Deprecated helpers (Moment only here)
// ============================

// Deprecated - for legacy code
export const momentToDateString = (m: Moment): string => m.format("YYYY-MM-DD")

// Deprecated - for legacy code
export const dateStringToMoment = (value: string): Moment =>
  moment(value, "YYYY-MM-DD", true)

// ============================
// MM-YYYY and YYYY-MM helpers
// ============================

const MM_YYYY_RE = /^(0[1-9]|1[0-2])-\d{4}$/
const YYYY_MM_RE = /^\d{4}-(0[1-9]|1[0-2])$/

export function isMMYYYY(i: string): boolean {
  return MM_YYYY_RE.test(i.trim())
}

export function parseMMYYYY(i: string): Date {
  const trimmed = i.trim()
  if (!isMMYYYY(trimmed)) {
    return new Date(NaN)
  }
  const [mm, yyyy] = trimmed.split("-")
  const year = Number(yyyy)
  const month = Number(mm)
  // last day of that month: day 0 of next month
  return new Date(year, month, 0)
}

export function isYYYYMM(i: string): boolean {
  return YYYY_MM_RE.test(i.trim())
}

export function parseYYYYMM(i: string): Date {
  const trimmed = i.trim()
  if (!isYYYYMM(trimmed)) {
    return new Date(NaN)
  }
  const [yyyy, mm] = trimmed.split("-")
  const year = Number(yyyy)
  const month = Number(mm)
  // last day of that month: day 0 of next month
  return new Date(year, month, 0)
}

// ============================
// Internal helpers (Date + date-fns only)
// ============================

const clampMonth = (m: number): number => Math.min(12, Math.max(1, m))

const normalizeMonths = (months: number[]): number[] => {
  const unique = Array.from(
    new Set(
      months
        .map((m) => clampMonth(m))
        .filter((m) => Number.isFinite(m))
    )
  )
  return unique.sort((a, b) => a - b)
}

/** Parse DateInput to Date. Uses parseMMYYYY for MM-YYYY strings. */
const toDateFromDateInput = (input: DateInput): Date => {
  if (typeof input === "string") {
    if (isMMYYYY(input)) {
      return parseMMYYYY(input)
    }
    if (isYYYYMM(input)) {
      return parseYYYYMM(input)
    }
  
    if (/^\d{4}-\d{2}-\d{2}$/.test(input.trim())) {
      return fromYMD(input)
    }
    return new Date(input)
  }
  if (typeof input === "number") {
    return new Date(input)
  }
  return input
}

/** Format Date as YYYY-MM-DD */
const toYMD = (d: Date): string => formatDate(d, "YYYY-MM-DD")


const fromYMD = (s: string): Date => {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(s.trim())
  if (!parts) {
    return new Date(s)
  }
  return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
}

/** Date as YYYY-MM (e.g. 2023-06) */
const toYearMonth = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`

/** Apply cap so no date exceeds capDate. Compares by day (startOfDay). */
const applyCap = (state: StateOutput, capDate: Date): StateOutput => {
  if (state.range.dateFrom === "" || state.range.dateTo === "") {
    return state
  }
  const capD = startOfDay(capDate)
  const fromD = startOfDay(fromYMD(state.range.dateFrom))
  const toD = startOfDay(fromYMD(state.range.dateTo))
  if (isNaN(fromD.getTime()) || isNaN(toD.getTime())) return state

  const cappedToD = isAfter(toD, capD) ? minDate(toD, capD) : toD
  const cappedFromD = isAfter(fromD, capD) ? capD : fromD

  // No capping needed: return state unchanged so yearMonths etc. stay as resolved
  if (isSameDay(cappedFromD, fromD) && isSameDay(cappedToD, toD)) {
    return state
  }

  const dateFrom = toYMD(cappedFromD)
  const dateTo = toYMD(cappedToD)
  const date = dateTo
  const year = cappedToD.getFullYear()
  const month = cappedToD.getMonth() + 1

  const months: number[] = []
  let cursor = startOfMonth(cappedFromD)
  const endCursor = endOfMonth(cappedToD)
  while (cursor.getTime() <= endCursor.getTime()) {
    if (cursor.getFullYear() === year) {
      months.push(cursor.getMonth() + 1)
    }
    cursor = addMonths(cursor, 1)
  }
  const yearMonths = { year, months: normalizeMonths(months) }

  return {
    range: { dateFrom, dateTo },
    year,
    yearMonths,
    yearMonth: { year, month, date: toYearMonth(cappedToD) },
    date,
  }
}

function parseCapDate (cap?: DateInput|true|null) : null|Date {
  if(!cap){
    return null
  }
  if(cap === true) return new Date()
  return new Date(cap)
}

// ============================
// Normalized state builder
// ============================

export const buildDateState = (
  input: StateInput,
  options?: ResolveDateOptions
): StateOutput => {
  const capDate = parseCapDate(options?.capDate)
  const raw = buildDateStateRaw(input)
  return capDate ? applyCap(raw, capDate) : raw
}

const buildDateStateRaw = (input: StateInput): StateOutput => {
  // 1) input.date
  if ("date" in input) {
    const d = toDateFromDateInput(input.date)
    const year = d.getFullYear()
    const month = d.getMonth() + 1
    const ymd = toYMD(d)
    // MM-YYYY or YYYY-MM: range = full month, date = last day of month
    const isMonthOnly =
      typeof input.date === "string" &&
      (isMMYYYY(input.date.trim()) || isYYYYMM(input.date.trim()))
    if (isMonthOnly) {
      const fromD = startOfMonth(d)
      const toD = endOfMonth(d)
      return {
        range: { dateFrom: toYMD(fromD), dateTo: toYMD(toD) },
        year,
        yearMonths: { year, months: [month] },
        yearMonth: { year, month, date: toYearMonth(d) },
        date: toYMD(toD),
      }
    }
    return {
      range: { dateFrom: ymd, dateTo: ymd },
      year,
      yearMonths: { year, months: [month] },
      yearMonth: { year, month, date: toYearMonth(d) },
      date: ymd,
    }
  }

  // 2) input.dateFrom + input.dateTo
  if ("dateFrom" in input && "dateTo" in input) {
    const fromD = toDateFromDateInput(input.dateFrom)
    const toD = toDateFromDateInput(input.dateTo)
    const minD = minDate(fromD, toD)
    const maxD = maxDate(fromD, toD)
    const dateFrom = toYMD(minD)
    const dateTo = toYMD(maxD)
    const year = maxD.getFullYear()
    const months: number[] = []
    let cursor = startOfMonth(minD)
    const endCursor = endOfMonth(maxD)
    while (cursor.getTime() <= endCursor.getTime()) {
      months.push(cursor.getMonth() + 1)
      cursor = addMonths(cursor, 1)
    }
    const normalizedMonths = normalizeMonths(months)
    const month = maxD.getMonth() + 1
    return {
      range: { dateFrom, dateTo },
      year,
      yearMonths: { year, months: normalizedMonths },
      yearMonth: { year, month, date: toYearMonth(maxD) },
      date: dateTo,
    }
  }

  // 3) input.year + input.months
  if ("year" in input && "months" in input) {
    const year = input.year
    const months = normalizeMonths(
      (input as { year: number; months: number[] }).months
    )
    const firstMonth = months[0]
    const lastMonth = months[months.length - 1]
    const fromD = new Date(year, firstMonth - 1, 1)
    const toD = endOfMonth(new Date(year, lastMonth - 1, 1))
    return {
      range: { dateFrom: toYMD(fromD), dateTo: toYMD(toD) },
      year,
      yearMonths: { year, months },
      yearMonth: { year, month: lastMonth, date: toYearMonth(toD) },
      date: toYMD(toD),
    }
  }

  // 4) input.year + input.month
  if ("year" in input && "month" in input) {
    const year = input.year
    const month = clampMonth((input as { year: number; month: number }).month)
    const fromD = new Date(year, month - 1, 1)
    const toD = endOfMonth(fromD)
    return {
      range: { dateFrom: toYMD(fromD), dateTo: toYMD(toD) },
      year,
      yearMonths: { year, months: [month] },
      yearMonth: { year, month, date: toYearMonth(toD) },
      date: toYMD(toD),
    }
  }

  // 5) input.year
  if ("year" in input) {
    const year = Number(input.year)
    const fromD = startOfYear(new Date(year, 0, 1))
    const toD = endOfYear(new Date(year, 11, 31))
    const months = Array.from({ length: 12 }, (_, i) => i + 1)
    return {
      range: { dateFrom: toYMD(fromD), dateTo: toYMD(toD) },
      year,
      yearMonths: { year, months },
      yearMonth: { year, month: 12, date: toYearMonth(toD) },
      date: toYMD(toD),
    }
  }

  // TypeScript exhaustiveness – StateInput covers all branches
  const fallbackYear = new Date().getFullYear()
  return {
    range: { dateFrom: "", dateTo: "" },
    year: fallbackYear,
    yearMonths: { year: fallbackYear, months: [] },
    yearMonth: { year: fallbackYear, month: 1, date: "" },
    date: "",
  }
}

// ============================
// Public API
// ============================

export const resolveDate = (
  input: StateInput,
  options?: ResolveDateOptions
): ObjOutput => {
  const state = buildDateState(input, options)

  const getRange = (format: OutputOption) => {
    if ((format as unknown) === Date) {
      return {
        dateFrom: fromYMD(state.range.dateFrom),
        dateTo: fromYMD(state.range.dateTo),
      }
    }
    const fmt = format as DateFormat
    return {
      dateFrom: formatDate(fromYMD(state.range.dateFrom), fmt) as OutputOption,
      dateTo: formatDate(fromYMD(state.range.dateTo), fmt) as OutputOption,
    }
  }

  const getSingleYear = () => state.year

  const getSingleMonth = () => ({
    year: state.yearMonth.year,
    month: state.yearMonth.month,
  })

  const getYearMonths = () => state.yearMonths

  const getSingleDay = (format: OutputOption): OutputOption => {
    if ((format as unknown) === Date) {
      return fromYMD(state.date) as OutputOption
    }
    return formatDate(fromYMD(state.date), format as DateFormat) as OutputOption
  }

  return {
    state,
    getRange,
    getSingleYear,
    getSingleMonth,
    getYearMonths,
    getSingleDay,
  }
}

export type { StateInput, StateOutput, ObjOutput, OutputOption }
